import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/cr";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Navigate, Link } from "react-router-dom";
import {
  ShieldAlert, Users, CreditCard, Activity, TrendingUp, Search, Trash2,
  ArrowUpCircle, ArrowDownCircle, ShieldCheck, RefreshCcw, Crown, Radar,
  UserPlus, Check, X, DollarSign,
} from "lucide-react";

const INPUT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]";

function Stat({ icon: Icon, label, value, accent = "#EAB308" }) {
  return (
    <div className="bg-[#0d0d0d] border border-[#262626] p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373]">{label}</span>
      </div>
      <div className="font-mono text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tab, setTab] = useState("users");

  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
    refetchInterval: 15000,
  });
  const { data: users = [] } = useQuery({
    queryKey: ["admin-users", search, statusFilter],
    queryFn: async () => (await api.get("/admin/users", { params: { q: search || undefined, status: statusFilter || undefined } })).data,
  });
  const { data: txns = [] } = useQuery({
    queryKey: ["admin-transactions"],
    queryFn: async () => (await api.get("/admin/transactions")).data,
  });
  const { data: stripeStatus } = useQuery({
    queryKey: ["admin-stripe"],
    queryFn: async () => (await api.get("/admin/stripe/status")).data,
  });
  const { data: licenses = [] } = useQuery({
    queryKey: ["admin-licenses"],
    queryFn: async () => (await api.get("/admin/licenses?limit=200")).data,
    refetchInterval: 30000,
  });
  const { data: waitlist = [] } = useQuery({
    queryKey: ["admin-waitlist"],
    queryFn: async () => (await api.get("/admin/waitlist?limit=200")).data,
    refetchInterval: 30000,
  });
  const approveUser = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/users/${id}/approve`)).data,
    onSuccess: () => {
      toast.success("User approved", { description: "They can now subscribe and activate the .exe" });
      qc.invalidateQueries({ queryKey: ["admin-waitlist"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    },
    onError: (e) => toast.error("Approve failed", { description: e?.response?.data?.detail }),
  });
  const rejectUser = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/users/${id}/reject`)).data,
    onSuccess: () => {
      toast.success("User rejected");
      qc.invalidateQueries({ queryKey: ["admin-waitlist"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error("Reject failed", { description: e?.response?.data?.detail }),
  });
  const revokeLicense = useMutation({
    mutationFn: async ({ user_id, device_id }) => (await api.post("/admin/licenses/revoke", { user_id, device_id })).data,
    onSuccess: () => { toast.success("Device revoked", { description: "Refresh will fail on this device within minutes" }); qc.invalidateQueries({ queryKey: ["admin-licenses"] }); },
  });
  const unrevokeLicense = useMutation({
    mutationFn: async ({ user_id, device_id }) => (await api.post("/admin/licenses/unrevoke", { user_id, device_id })).data,
    onSuccess: () => { toast.success("Device restored"); qc.invalidateQueries({ queryKey: ["admin-licenses"] }); },
  });

  const grant = useMutation({
    mutationFn: async ({ id, days }) => (await api.post(`/admin/users/${id}/grant`, { days, reason: "Admin grant" })).data,
    onSuccess: () => { toast.success("Subscription granted"); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
  });
  const revoke = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/users/${id}/revoke`)).data,
    onSuccess: () => { toast.success("Subscription revoked"); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
  });
  const setRole = useMutation({
    mutationFn: async ({ id, role }) => (await api.post(`/admin/users/${id}/role`, { role })).data,
    onSuccess: () => { toast.success("Role updated"); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
  });
  const deleteUser = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/users/${id}`)).data,
    onSuccess: () => { toast.success("User deleted"); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
  });
  const setCustomPrice = useMutation({
    mutationFn: async ({ id, price_usd, price_aud, note }) =>
      (await api.post(`/admin/users/${id}/custom-price`, { price_usd, price_aud, note })).data,
    onSuccess: (_d, vars) => {
      const cleared = vars.price_usd == null && vars.price_aud == null;
      toast.success(cleared ? "Custom price cleared" : "Custom price saved", {
        description: cleared
          ? "User now pays standard pricing"
          : `USD: ${vars.price_usd ?? "—"} · AUD: ${vars.price_aud ?? "—"}`,
      });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error("Failed", { description: e?.response?.data?.detail }),
  });
  const setWebhookSecret = useMutation({
    mutationFn: async (secret) => (await api.post("/admin/stripe/webhook-secret", { webhook_secret: secret })).data,
    onSuccess: () => { toast.success("Webhook secret saved"); qc.invalidateQueries({ queryKey: ["admin-stripe"] }); },
    onError: (e) => toast.error("Failed", { description: e?.response?.data?.detail }),
  });
  const setApiKey = useMutation({
    mutationFn: async (key) => (await api.post("/admin/stripe/api-key", { api_key: key })).data,
    onSuccess: () => { toast.success("Stripe API key saved", { description: "Encrypted at rest via Fernet vault" }); qc.invalidateQueries({ queryKey: ["admin-stripe"] }); },
    onError: (e) => toast.error("Invalid key", { description: e?.response?.data?.detail }),
  });
  const testStripe = useMutation({
    mutationFn: async () => (await api.post("/admin/stripe/test")).data,
    onSuccess: (data) => {
      if (data.ok) {
        const bal = (data.available || []).map(a => `${a.currency} ${a.amount.toFixed(2)}`).join(", ");
        toast.success(`Stripe connection OK (${data.mode})`, {
          description: data.message || (bal ? `Available balance: ${bal}` : "Read-only ping succeeded"),
        });
      } else {
        toast.error("Stripe test failed", { description: data.error || "Unknown error" });
      }
    },
    onError: (e) => toast.error("Test failed", { description: e?.response?.data?.detail }),
  });
  const setPricing = useMutation({
    mutationFn: async (body) => (await api.post("/admin/stripe/pricing", body)).data,
    onSuccess: () => { toast.success("Pricing updated"); qc.invalidateQueries({ queryKey: ["admin-stripe"] }); },
    onError: (e) => toast.error("Failed", { description: e?.response?.data?.detail }),
  });

  const [webhookSecret, setWebhookSecretInput] = useState("");
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [priceUsd, setPriceUsd] = useState("");
  const [priceAud, setPriceAud] = useState("");

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-[#050505] grain text-white">
      {/* Top bar */}
      <header className="border-b border-[#262626] bg-[#0a0a0a] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border border-[#EF4444] flex items-center justify-center bg-black">
              <Crown className="w-4 h-4 text-[#EF4444]" />
            </div>
            <div>
              <div className="font-heading text-lg font-black tracking-tighter uppercase">
                Admin <span className="text-[#EF4444]">portal</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#737373] font-mono">
                {user?.email}
              </div>
            </div>
          </div>
          <Link
            to="/app"
            data-testid="admin-exit"
            className="px-3 py-2 text-xs uppercase tracking-wider font-bold border border-[#262626] hover:border-[#EAB308] text-white flex items-center gap-1.5"
          >
            <Radar className="w-3.5 h-3.5" /> Back to app
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6" data-testid="admin-stats">
          <Stat icon={Users} label="Total users" value={stats?.users?.total ?? 0} accent="#3B82F6" />
          <Stat icon={ShieldCheck} label="Active subs" value={stats?.users?.active ?? 0} accent="#22C55E" />
          <Stat
            icon={TrendingUp}
            label="Revenue (USD)"
            value={`$${(stats?.revenue?.all_time_by_currency?.USD ?? 0).toFixed(0)}`}
            accent="#EAB308"
          />
          <Stat
            icon={TrendingUp}
            label="Revenue (AUD)"
            value={`A$${(stats?.revenue?.all_time_by_currency?.AUD ?? 0).toFixed(0)}`}
            accent="#EAB308"
          />
          <Stat icon={Activity} label="Purchase attempts" value={stats?.platform?.purchase_attempts ?? 0} accent="#A855F7" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#262626] mb-6" data-testid="admin-tabs">
          {[
            { id: "users", label: "Users", icon: Users },
            { id: "waitlist", label: "Waitlist", icon: UserPlus },
            { id: "transactions", label: "Transactions", icon: CreditCard },
            { id: "licenses", label: "Licenses", icon: Crown },
            { id: "stripe", label: "Stripe", icon: ShieldAlert },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 transition-colors ${
                tab === t.id ? "text-white border-[#EF4444]" : "text-[#A3A3A3] border-transparent hover:text-white hover:border-[#262626]"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "users" && (
          <section data-testid="admin-users-section">
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-[#737373]" />
                <input
                  data-testid="admin-user-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email…"
                  className={`${INPUT} pl-9`}
                />
              </div>
              <select
                data-testid="admin-user-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={INPUT}
                style={{ maxWidth: 180 }}
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="canceled">Canceled</option>
                <option value="past_due">Past due</option>
              </select>
            </div>

            <div className="border border-[#262626] rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead className="bg-[#0d0d0d] border-b border-[#262626]">
                  <tr className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373]">
                    <th className="text-left px-3 py-2.5">Email</th>
                    <th className="text-left px-3 py-2.5">Role</th>
                    <th className="text-left px-3 py-2.5">Subscription</th>
                    <th className="text-left px-3 py-2.5">Plan</th>
                    <th className="text-left px-3 py-2.5">Expires</th>
                    <th className="text-left px-3 py-2.5">Joined</th>
                    <th className="text-right px-3 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-[#1A1A1A] hover:bg-[#0d0d0d]">
                      <td className="px-3 py-2.5 text-white font-mono text-xs">{u.email}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm uppercase ${
                          u.role === "admin" ? "text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10" : "text-[#A3A3A3] border-[#262626]"
                        }`}>{u.role}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm uppercase ${
                          u.subscription_status === "active"
                            ? "text-[#22C55E] border-[#22C55E]/40 bg-[#22C55E]/10"
                            : "text-[#A3A3A3] border-[#262626]"
                        }`}>{u.subscription_status}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        {(u.custom_price_usd != null || u.custom_price_aud != null) ? (
                          <div className="flex flex-col gap-0.5" title={u.custom_price_note || "Custom price"}>
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm uppercase w-fit ${
                              (u.custom_price_usd === 0 || u.custom_price_aud === 0)
                                ? "text-[#A855F7] border-[#A855F7]/40 bg-[#A855F7]/10"
                                : "text-[#EAB308] border-[#EAB308]/40 bg-[#EAB308]/10"
                            }`}>
                              {(u.custom_price_usd === 0 || u.custom_price_aud === 0) ? "FREE" : "CUSTOM"}
                            </span>
                            <span className="text-[10px] font-mono text-[#A3A3A3]">
                              {u.custom_price_usd != null ? `$${u.custom_price_usd}` : "—"}{" / "}
                              {u.custom_price_aud != null ? `A$${u.custom_price_aud}` : "—"}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] font-mono text-[#525252]">standard</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs font-mono text-[#A3A3A3]">
                        {u.current_period_end ? new Date(u.current_period_end).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-xs font-mono text-[#737373]">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            data-testid={`admin-grant-${u.id}`}
                            onClick={() => grant.mutate({ id: u.id, days: 30 })}
                            className="p-1.5 text-[#22C55E] hover:bg-[#22C55E]/10 rounded-sm"
                            title="Grant 30 days"
                          >
                            <ArrowUpCircle className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`admin-revoke-${u.id}`}
                            onClick={() => revoke.mutate(u.id)}
                            className="p-1.5 text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded-sm"
                            title="Revoke"
                          >
                            <ArrowDownCircle className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`admin-price-${u.id}`}
                            onClick={() => {
                              const cur = u.custom_price_usd != null || u.custom_price_aud != null;
                              const action = window.prompt(
                                `Custom price for ${u.email}\n\n` +
                                `Type a number (e.g. 50 for $50/mo, 0 for FREE) or "clear" to reset to standard pricing.\n\n` +
                                `Current: ${u.custom_price_usd != null ? `$${u.custom_price_usd} USD` : "standard"}`,
                                cur ? String(u.custom_price_usd ?? "") : ""
                              );
                              if (action == null) return;
                              const trimmed = action.trim().toLowerCase();
                              if (trimmed === "" || trimmed === "clear" || trimmed === "reset") {
                                setCustomPrice.mutate({ id: u.id, price_usd: null, price_aud: null, note: null });
                                return;
                              }
                              const usd = Number(trimmed);
                              if (Number.isNaN(usd) || usd < 0) {
                                toast.error("Invalid price", { description: "Must be a non-negative number or 'clear'" });
                                return;
                              }
                              const aud = window.prompt(`AUD price for ${u.email}? (Enter for ${(usd * 1.55).toFixed(0)})`, String(Math.round(usd * 1.55)));
                              if (aud == null) return;
                              const audNum = aud.trim() === "" ? Math.round(usd * 1.55) : Number(aud);
                              if (Number.isNaN(audNum) || audNum < 0) {
                                toast.error("Invalid AUD price"); return;
                              }
                              const note = window.prompt(`Internal note (e.g. "friend discount")`, u.custom_price_note || "") || null;
                              setCustomPrice.mutate({ id: u.id, price_usd: usd, price_aud: audNum, note });
                            }}
                            className="p-1.5 text-[#EAB308] hover:bg-[#EAB308]/10 rounded-sm"
                            title={u.custom_price_usd != null ? `Custom: $${u.custom_price_usd}` : "Set custom price"}
                          >
                            <DollarSign className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`admin-role-${u.id}`}
                            onClick={() => setRole.mutate({ id: u.id, role: u.role === "admin" ? "user" : "admin" })}
                            className="p-1.5 text-[#A855F7] hover:bg-[#A855F7]/10 rounded-sm"
                            title={u.role === "admin" ? "Demote to user" : "Promote to admin"}
                          >
                            <Crown className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`admin-delete-${u.id}`}
                            onClick={() => { if (window.confirm(`Delete user ${u.email}?`)) deleteUser.mutate(u.id); }}
                            className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-sm"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-10 text-center text-[#737373] text-sm">No users match</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "waitlist" && (
          <section data-testid="admin-waitlist-section">
            <div className="bg-[#EAB308]/5 border border-[#EAB308]/30 px-3 py-2 rounded-sm mb-4 text-[11px] text-[#D4D4D4] leading-relaxed">
              <strong className="text-white">Signup gate.</strong> Every new account lands here as <code className="text-[#EAB308]">pending</code>. They can&apos;t subscribe, can&apos;t activate the .exe, can&apos;t hit the scan engine until you approve. Reject = blacklisted email.
            </div>
            <div className="border border-[#262626] rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-waitlist-table">
                <thead className="bg-[#0d0d0d] border-b border-[#262626]">
                  <tr className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373]">
                    <th className="text-left px-3 py-2.5">Email</th>
                    <th className="text-left px-3 py-2.5">Name</th>
                    <th className="text-left px-3 py-2.5">Discord</th>
                    <th className="text-left px-3 py-2.5">Joined</th>
                    <th className="text-left px-3 py-2.5">Status</th>
                    <th className="text-right px-3 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {waitlist.map((u) => (
                    <tr key={u.id} data-testid={`admin-waitlist-row-${u.id}`} className="border-b border-[#1A1A1A] hover:bg-[#0d0d0d]">
                      <td className="px-3 py-2.5 text-white font-mono text-xs">{u.email}</td>
                      <td className="px-3 py-2.5 text-[#A3A3A3] text-xs">{u.name || "—"}</td>
                      <td className="px-3 py-2.5 text-xs">
                        {u.discord_username ? (
                          <button
                            data-testid={`admin-waitlist-discord-${u.id}`}
                            onClick={() => {
                              navigator.clipboard.writeText(u.discord_username);
                              toast.success("Discord handle copied", { description: u.discord_username });
                            }}
                            className="font-mono text-[#5865F2] hover:text-[#7289DA] underline decoration-dotted underline-offset-2"
                            title="Click to copy"
                          >
                            {u.discord_username}
                          </button>
                        ) : (
                          <span className="text-[#525252] font-mono">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs font-mono text-[#737373]">
                        {new Date(u.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-[10px] font-mono px-1.5 py-0.5 border rounded-sm uppercase text-[#EAB308] border-[#EAB308]/40 bg-[#EAB308]/10">
                          {u.approval_status}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            data-testid={`admin-waitlist-approve-${u.id}`}
                            onClick={() => approveUser.mutate(u.id)}
                            disabled={approveUser.isPending}
                            className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 border border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/10 rounded-sm flex items-center gap-1 disabled:opacity-50"
                          >
                            <Check className="w-3 h-3" /> Approve
                          </button>
                          <button
                            data-testid={`admin-waitlist-reject-${u.id}`}
                            onClick={() => { if (window.confirm(`Reject ${u.email}? They will be blocked from accessing the app.`)) rejectUser.mutate(u.id); }}
                            disabled={rejectUser.isPending}
                            className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-sm flex items-center gap-1 disabled:opacity-50"
                          >
                            <X className="w-3 h-3" /> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {waitlist.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-10 text-center text-[#737373] text-sm">Waitlist is empty — no pending signups</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "transactions" && (
          <section data-testid="admin-transactions-section">
            <div className="border border-[#262626] rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-transactions-table">
                <thead className="bg-[#0d0d0d] border-b border-[#262626]">
                  <tr className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373]">
                    <th className="text-left px-3 py-2.5">When</th>
                    <th className="text-left px-3 py-2.5">User</th>
                    <th className="text-right px-3 py-2.5">Amount</th>
                    <th className="text-left px-3 py-2.5">Status</th>
                    <th className="text-left px-3 py-2.5">Session ID</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.map((t) => (
                    <tr key={t.session_id} data-testid={`admin-txn-row-${t.session_id}`} className="border-b border-[#1A1A1A] hover:bg-[#0d0d0d]">
                      <td className="px-3 py-2.5 text-xs font-mono text-[#A3A3A3]">{new Date(t.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2.5 text-xs font-mono text-white">{t.email}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-white">
                        {t.currency === "usd" ? "$" : "A$"}{Number(t.amount).toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm uppercase ${
                          t.payment_status === "paid"
                            ? "text-[#22C55E] border-[#22C55E]/40 bg-[#22C55E]/10"
                            : "text-[#A3A3A3] border-[#262626]"
                        }`}>{t.payment_status}</span>
                      </td>
                      <td className="px-3 py-2.5 text-[10px] font-mono text-[#525252] truncate max-w-[200px]">{t.session_id}</td>
                    </tr>
                  ))}
                  {txns.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-10 text-center text-[#737373] text-sm">No transactions yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "licenses" && (
          <section data-testid="admin-licenses-section">
            <div className="bg-[#A855F7]/5 border border-[#A855F7]/30 px-3 py-2 rounded-sm mb-4 text-[11px] text-[#D4D4D4] leading-relaxed">
              <strong className="text-white">Desktop activations.</strong> Every row is a .exe install that signed in with their subscriber account. Revoke a device to lock it out — next time the .exe phones home (within ~4h), it&apos;ll get a 403 and refuse to open the Scalp page.
            </div>
            <div className="border border-[#262626] rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-licenses-table">
                <thead className="bg-[#0d0d0d] border-b border-[#262626]">
                  <tr className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373]">
                    <th className="text-left px-3 py-2.5">When</th>
                    <th className="text-left px-3 py-2.5">User</th>
                    <th className="text-left px-3 py-2.5">Device</th>
                    <th className="text-left px-3 py-2.5">IP</th>
                    <th className="text-left px-3 py-2.5">Status</th>
                    <th className="text-right px-3 py-2.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((l) => (
                    <tr key={`${l.id}`} data-testid={`admin-license-row-${l.id}`} className={`border-b border-[#1A1A1A] hover:bg-[#0d0d0d] ${l.revoked ? "opacity-50" : ""}`}>
                      <td className="px-3 py-2.5 text-xs font-mono text-[#A3A3A3]">{new Date(l.activated_at).toLocaleString()}</td>
                      <td className="px-3 py-2.5 text-xs font-mono text-white">{l.user_email}</td>
                      <td className="px-3 py-2.5">
                        <div className="text-xs font-mono text-white">{l.device_name || "unknown"}</div>
                        <div className="text-[10px] font-mono text-[#525252]">{l.device_id}</div>
                      </td>
                      <td className="px-3 py-2.5 text-[10px] font-mono text-[#737373]">{l.client_ip || "—"}</td>
                      <td className="px-3 py-2.5">
                        {l.revoked ? (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 border rounded-sm uppercase text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10">Revoked</span>
                        ) : (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 border rounded-sm uppercase text-[#22C55E] border-[#22C55E]/40 bg-[#22C55E]/10">Active</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {l.revoked ? (
                          <button
                            data-testid={`admin-license-unrevoke-${l.id}`}
                            onClick={() => unrevokeLicense.mutate({ user_id: l.user_id, device_id: l.device_id })}
                            className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 border border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/10 rounded-sm"
                          >
                            Restore
                          </button>
                        ) : (
                          <button
                            data-testid={`admin-license-revoke-${l.id}`}
                            onClick={() => { if (confirm(`Revoke ${l.device_name || l.device_id}? This locks the .exe.`)) revokeLicense.mutate({ user_id: l.user_id, device_id: l.device_id }); }}
                            className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-sm"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {licenses.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-10 text-center text-[#737373] text-sm">No desktop activations yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "stripe" && (
          <section data-testid="admin-stripe-section" className="space-y-4">
            <div className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm">
              <h3 className="font-heading text-base font-bold uppercase tracking-wider mb-4">Stripe configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1">API key</div>
                  <div className="font-mono text-white">{stripeStatus?.api_key_masked || "Not set"}</div>
                  <div className="text-[11px] text-[#A3A3A3] mt-1">
                    Mode: <span className={
                      stripeStatus?.api_key_mode === "live" ? "text-[#22C55E]" :
                      stripeStatus?.api_key_mode === "test" ? "text-[#EAB308]" :
                      stripeStatus?.api_key_mode === "emergent" ? "text-[#A855F7]" : "text-[#737373]"
                    }>{stripeStatus?.api_key_mode}</span> · Source: {stripeStatus?.api_key_source}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1">Webhook secret</div>
                  <div className="font-mono text-white">{stripeStatus?.webhook_secret_set ? "Configured" : "Not set"}</div>
                  <div className="text-[11px] text-[#A3A3A3] mt-1">Endpoint: <code className="text-[#EAB308]">{stripeStatus?.webhook_endpoint}</code></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1">Pricing</div>
                  <div className="font-mono text-white">${stripeStatus?.pricing_usd?.toFixed(0)} USD · A${stripeStatus?.pricing_aud?.toFixed(0)} AUD</div>
                  <div className="text-[11px] text-[#A3A3A3] mt-1">per 30 days</div>
                </div>
              </div>
            </div>

            <div className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-base font-bold uppercase tracking-wider">Stripe Secret Key</h3>
                <span className="text-[10px] uppercase tracking-[0.2em] font-mono font-bold text-[#22C55E] flex items-center gap-1.5" title="Encrypted at rest in MongoDB using the Fernet vault (AES-128)">
                  <ShieldCheck className="w-3 h-3" /> Encrypted at rest
                </span>
              </div>
              <p className="text-[11px] text-[#737373] mb-3">
                From <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#EAB308] hover:underline">dashboard.stripe.com/apikeys</a>. <strong className="text-[#FBBF24]">Never share this key publicly</strong>. Test keys start with <code className="text-[#A3A3A3]">sk_test_</code>, live with <code className="text-[#22C55E]">sk_live_</code>.
              </p>
              <div className="flex gap-2">
                <input
                  data-testid="admin-api-key-input"
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder={stripeStatus?.api_key_masked || "sk_test_..."}
                  className={INPUT + " font-mono"}
                />
                <button
                  data-testid="admin-api-key-save"
                  disabled={!apiKeyInput || setApiKey.isPending}
                  onClick={() => { setApiKey.mutate(apiKeyInput); setApiKeyInput(""); }}
                  className="px-4 py-2 text-xs uppercase font-bold tracking-wider bg-[#EAB308] text-black hover:bg-[#FDE047] rounded-sm disabled:opacity-50"
                >
                  Save key
                </button>
                <button
                  data-testid="admin-api-key-test"
                  disabled={!stripeStatus?.api_key_set || testStripe.isPending}
                  onClick={() => testStripe.mutate()}
                  title="Pings Stripe's /v1/balance endpoint with the stored key (read-only, no charges)"
                  className="px-4 py-2 text-xs uppercase font-bold tracking-wider bg-[#0d0d0d] text-white border border-[#22C55E] hover:bg-[#22C55E]/10 rounded-sm disabled:opacity-30 flex items-center gap-1.5"
                >
                  {testStripe.isPending ? <RefreshCcw className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
                  Test connection
                </button>
              </div>
            </div>

            <div className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm">
              <h3 className="font-heading text-base font-bold uppercase tracking-wider mb-2">Set webhook secret</h3>
              <p className="text-[11px] text-[#737373] mb-3">
                Paste the signing secret from Stripe Dashboard → Developers → Webhooks → reveal. Format: <code className="text-[#A3A3A3]">whsec_…</code>
              </p>
              <div className="flex gap-2">
                <input
                  data-testid="admin-webhook-secret-input"
                  type="password"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecretInput(e.target.value)}
                  placeholder={stripeStatus?.webhook_secret_masked || "whsec_..."}
                  className={INPUT + " font-mono"}
                />
                <button
                  data-testid="admin-webhook-secret-save"
                  disabled={!webhookSecret || setWebhookSecret.isPending}
                  onClick={() => { setWebhookSecret.mutate(webhookSecret); setWebhookSecretInput(""); }}
                  className="px-4 py-2 text-xs uppercase font-bold tracking-wider bg-[#EAB308] text-black hover:bg-[#FDE047] rounded-sm disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </div>

            <div className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm">
              <h3 className="font-heading text-base font-bold uppercase tracking-wider mb-2">Pricing</h3>
              <p className="text-[11px] text-[#737373] mb-3">
                Charged to users when they buy a 30-day access pass. Changes apply to next checkout — existing passes are unaffected.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5">Price USD / month</div>
                  <input
                    data-testid="admin-price-usd-input"
                    type="number"
                    min="1"
                    value={priceUsd}
                    onChange={(e) => setPriceUsd(e.target.value)}
                    placeholder={String(stripeStatus?.pricing_usd ?? 200)}
                    className={INPUT + " font-mono"}
                  />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5">Price AUD / month</div>
                  <input
                    data-testid="admin-price-aud-input"
                    type="number"
                    min="1"
                    value={priceAud}
                    onChange={(e) => setPriceAud(e.target.value)}
                    placeholder={String(stripeStatus?.pricing_aud ?? 310)}
                    className={INPUT + " font-mono"}
                  />
                </div>
              </div>
              <button
                data-testid="admin-pricing-save"
                disabled={(!priceUsd && !priceAud) || setPricing.isPending}
                onClick={() => {
                  setPricing.mutate({
                    price_usd: Number(priceUsd || stripeStatus?.pricing_usd),
                    price_aud: Number(priceAud || stripeStatus?.pricing_aud),
                  });
                  setPriceUsd(""); setPriceAud("");
                }}
                className="mt-3 px-4 py-2 text-xs uppercase font-bold tracking-wider bg-[#EAB308] text-black hover:bg-[#FDE047] rounded-sm disabled:opacity-50"
              >
                Update pricing
              </button>
            </div>

            <div className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm">
              <h3 className="font-heading text-base font-bold uppercase tracking-wider mb-3">Switch to live keys (production)</h3>
              <ol className="space-y-2 text-sm text-[#A3A3A3] list-decimal list-inside">
                <li>Get your live API key from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#EAB308] hover:underline">Stripe Dashboard → API keys</a></li>
                <li>Edit <code className="text-[#A3A3A3]">/app/backend/.env</code>: set <code className="text-[#EAB308]">STRIPE_API_KEY=sk_live_...</code></li>
                <li>Create a webhook at Stripe Dashboard → Webhooks pointing to <code className="text-[#EAB308]">{window.location.origin}/api/billing/webhook/stripe</code></li>
                <li>Copy the signing secret and save it above (or set <code className="text-[#A3A3A3]">STRIPE_WEBHOOK_SECRET</code> in .env)</li>
                <li>Restart the backend: <code className="text-[#A3A3A3]">sudo supervisorctl restart backend</code></li>
              </ol>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
